import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "../lib/api";
import { capture, EVENTS } from "../lib/analytics";

// Drop empty/false params so they don't pollute the query key or the request.
function cleanParams(params = {}) {
  const out = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "" && v !== false) out[k] = v;
  }
  return out;
}

const STALE = {
  fast: 30_000,
  medium: 2 * 60_000,
  slow: 5 * 60_000,
};

// ── Links ────────────────────────────────────────────────────────────────────

export function useLinks() {
  return useQuery({
    queryKey: ["links"],
    queryFn: () => api.get("/links").then((r) => r.data),
    staleTime: STALE.fast,
  });
}

// Server-driven, paginated link list for the redesigned Links library. Keyed
// under ["links", …] so the existing create/delete/patch/bulk mutations (which
// invalidate ["links"]) refresh it automatically. Reads the X-Next-Cursor
// response header for keyset-style paging (50/page).
export function useLinksInfinite(params = {}, enabled = true) {
  const clean = cleanParams(params);
  return useInfiniteQuery({
    queryKey: ["links", "infinite", clean],
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const r = await api.get("/links", {
        params: { ...clean, limit: 50, cursor: pageParam || undefined },
      });
      return { items: r.data, nextCursor: r.headers["x-next-cursor"] || null };
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: STALE.fast,
    enabled,
  });
}

// Filtered total for the library header ("1,204 links"). Same filter params.
export function useLinksCount(params = {}, enabled = true) {
  const clean = cleanParams(params);
  return useQuery({
    queryKey: ["links", "count", clean],
    queryFn: () => api.get("/links/count", { params: clean }).then((r) => r.data.count),
    staleTime: STALE.fast,
    enabled,
  });
}

export function useCreateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post("/links", payload).then((r) => r.data),
    onSuccess: (data) => {
      capture(EVENTS.LINK_CREATED, {
        has_alias: !!data?.custom_alias || !!data?.code,
        has_routing: !!data?.routing,
        has_campaign: !!data?.campaign_id,
      });
      qc.invalidateQueries({ queryKey: ["links"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["tags"] });
    },
  });
}

export function useDeleteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId) => api.delete(`/links/${linkId}`),
    onMutate: async (linkId) => {
      await qc.cancelQueries({ queryKey: ["links"] });
      const prev = qc.getQueryData(["links"]);
      qc.setQueryData(["links"], (old) => (old ? old.filter((l) => l.id !== linkId) : old));
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(["links"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["links"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function usePatchLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, patch }) => api.patch(`/links/${linkId}`, patch).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["links"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["tags"] });
    },
  });
}

// ── Stats ────────────────────────────────────────────────────────────────────

export function useStats() {
  return useQuery({
    queryKey: ["stats", "overview"],
    queryFn: () => api.get("/stats/overview").then((r) => r.data),
    staleTime: STALE.fast,
  });
}

export function useAccountAnalytics(days = 14, enabled = true) {
  return useQuery({
    queryKey: ["stats", "analytics", days],
    queryFn: () => api.get("/stats/analytics", { params: { days } }).then((r) => r.data),
    staleTime: STALE.fast,
    enabled,
  });
}

export function useActivity(limit = 12) {
  return useQuery({
    queryKey: ["stats", "activity", limit],
    queryFn: () => api.get("/stats/activity", { params: { limit } }).then((r) => r.data),
    staleTime: STALE.fast,
  });
}

export function useInsights(days = 7, enabled = true) {
  return useQuery({
    queryKey: ["stats", "insights", days],
    queryFn: () => api.get("/stats/insights", { params: { days } }).then((r) => r.data),
    staleTime: STALE.fast,
    enabled,
  });
}

// ── Campaigns ────────────────────────────────────────────────────────────────

export function useCampaigns() {
  return useQuery({
    queryKey: ["campaigns"],
    queryFn: () => api.get("/campaigns").then((r) => r.data),
    staleTime: STALE.medium,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post("/campaigns", payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

export function usePatchCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/campaigns/${id}`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/campaigns/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["campaigns"] });
      const prev = qc.getQueryData(["campaigns"]);
      qc.setQueryData(["campaigns"], (old) => (old ? old.filter((c) => c.id !== id) : old));
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(["campaigns"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["campaigns"] }),
  });
}

// ── Domains ──────────────────────────────────────────────────────────────────

export function useDomains(enabled = true) {
  return useQuery({
    queryKey: ["domains"],
    queryFn: () => api.get("/domains").then((r) => r.data),
    staleTime: STALE.slow,
    enabled,
  });
}

export function useCreateDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post("/domains", payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

export function useVerifyDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/domains/${id}/verify`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

export function useDeleteDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/domains/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["domains"] });
      const prev = qc.getQueryData(["domains"]);
      qc.setQueryData(["domains"], (old) => (old ? old.filter((d) => d.id !== id) : old));
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(["domains"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["domains"] }),
  });
}

// ── Tags ─────────────────────────────────────────────────────────────────────

export function useTags() {
  return useQuery({
    queryKey: ["tags"],
    queryFn: () => api.get("/tags").then((r) => r.data),
    staleTime: STALE.medium,
  });
}

// ── Analytics ────────────────────────────────────────────────────────────────

export function useLinkAnalytics(linkId, days) {
  return useQuery({
    queryKey: ["links", linkId, "analytics", days ?? "all"],
    queryFn: () =>
      api.get(`/links/${linkId}/analytics${days ? `?days=${days}` : ""}`).then((r) => r.data),
    enabled: !!linkId,
    staleTime: STALE.fast,
  });
}

export function useCampaignAnalytics(campaignId, params) {
  return useQuery({
    queryKey: ["campaigns", campaignId, "analytics", params],
    queryFn: () => api.get(`/campaigns/${campaignId}/analytics`, { params }).then((r) => r.data),
    enabled: !!campaignId,
    staleTime: STALE.fast,
  });
}

// ── Bulk ─────────────────────────────────────────────────────────────────────

export function useBulkDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (link_ids) => api.post("/bulk/links/delete", { link_ids }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["links"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useBulkPatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.patch("/bulk/links", payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["links"] });
      qc.invalidateQueries({ queryKey: ["tags"] });
    },
  });
}

// ── Webhooks ────────────────────────────────────────────────────────────────

export function useWebhooks(enabled = true) {
  return useQuery({
    queryKey: ["webhooks"],
    queryFn: () => api.get("/webhooks").then((r) => r.data),
    staleTime: STALE.medium,
    enabled,
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post("/webhooks", payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks"] }),
  });
}

export function usePatchWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/webhooks/${id}`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks"] }),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/webhooks/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["webhooks"] });
      const prev = qc.getQueryData(["webhooks"]);
      qc.setQueryData(["webhooks"], (old) => (old ? old.filter((w) => w.id !== id) : old));
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(["webhooks"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["webhooks"] }),
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: (id) => api.post(`/webhooks/${id}/test`),
  });
}

// ── API Keys ────────────────────────────────────────────────────────────────

export function useApiKeys(enabled = true) {
  return useQuery({
    queryKey: ["api-keys"],
    queryFn: () => api.get("/api-keys").then((r) => r.data),
    staleTime: STALE.medium,
    enabled,
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post("/api-keys", payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/api-keys/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["api-keys"] });
      const prev = qc.getQueryData(["api-keys"]);
      qc.setQueryData(["api-keys"], (old) => (old ? old.filter((k) => k.id !== id) : old));
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(["api-keys"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

// ── Integrations ────────────────────────────────────────────────────────────

export function useIntegrations(enabled = true) {
  return useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.get("/integrations").then((r) => r.data),
    staleTime: STALE.medium,
    enabled,
  });
}

export function useCreateIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post("/integrations", payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  });
}

export function usePatchIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/integrations/${id}`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  });
}

export function useDeleteIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/integrations/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["integrations"] });
      const prev = qc.getQueryData(["integrations"]);
      qc.setQueryData(["integrations"], (old) => (old ? old.filter((i) => i.id !== id) : old));
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(["integrations"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  });
}

export function useTestIntegration() {
  return useMutation({
    mutationFn: (id) => api.post(`/integrations/${id}/test`),
  });
}

// ── Team ────────────────────────────────────────────────────────────────────

export function useTeamMembers(enabled = true) {
  return useQuery({
    queryKey: ["team", "members"],
    queryFn: () => api.get("/team/members").then((r) => r.data),
    staleTime: STALE.medium,
    enabled,
  });
}

export function useInviteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post("/team/invite", payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team", "members"] }),
  });
}

export function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId) => api.delete(`/team/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team", "members"] }),
  });
}

export function useChangeMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }) =>
      api.patch(`/team/members/${userId}/role`, { role }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team", "members"] }),
  });
}

export function useLeaveTeam() {
  return useMutation({
    mutationFn: () => api.post("/team/leave"),
  });
}

// ── Campaign Detail ─────────────────────────────────────────────────────────

export function useCampaignDetail(campaignId) {
  return useQuery({
    queryKey: ["campaigns", campaignId, "detail"],
    queryFn: () => api.get(`/campaigns/${campaignId}`).then((r) => r.data),
    enabled: !!campaignId,
    staleTime: STALE.fast,
  });
}
